import { type ReactElement, useMemo } from 'react';
import { type FleetPresenceView, type PeriodDashboardView, type WorkLineSummaryView, type WorkLineView, WORK_LINE_VIEWS } from '@vello/api-client';
import { BarList, Notice, PageHeader, Panel, SectionTitle, Grid, Stack, Text } from '@vello/web-theme';
import { PRESENCES, adminScreenMarket, adminScreenMarketChoices, marketTimeZone, presenceTone, presetPeriod } from '../../src/index';
import { CountCard } from '../components/count-card';
import { StatusPanel, useRealmCounts } from '../components/status-panel';
import { useRead } from '../components/use-read';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';

/** One grid for the KPI cards and one for the panels, so the layout does not move when the data lands. */
/**
 * Prestadores › Painel (DevMobility C-031, recovering the D471 descope; V-OPS-CHARTS, D513): the providers' base counts from
 * what the API already returns — drivers and merchants by status (the directory's `total` with `limit=1`, scoped by the server),
 * the driver review queue of the market, and the drivers online right now (the period read's `supplyNow`, which only a market
 * scope has). Nothing is computed that the API does not say: no supply/demand ratio, no ETA, no net revenue (wave 10, D488).
 * A read that fails or is refused reads "unavailable" with a retry, never a zero.
 * D621: "Prestadores por atuação" counts the market's partners per line of work (car rides, moto taxi, food, parcels…); a
 * person who works in several lines counts in each (the panel says so) and "nada habilitado ainda" counts the rest.
 */

/** The lines always listed (a market's core offer); any other line shows only when someone works in it. */
const CORE_LINES: readonly WorkLineView[] = ['RIDE_CAR', 'RIDE_MOTO', 'FOOD', 'SEND'];
export function ProvidersDashboardScreen(): ReactElement {
  const { runtime, scope, markets } = useAdmin();
  const { t, lang } = useT();
  const { v } = useVocab();
  const choices = useMemo(() => adminScreenMarketChoices(scope, markets), [scope, markets]);
  const market = adminScreenMarket(scope, choices, undefined);
  const timeZone = marketTimeZone(market, markets);

  const drivers = useRealmCounts('DRIVER');
  const merchants = useRealmCounts('MERCHANT');
  // The review queue and the live supply are one market's: held back (not guessed) when the scope names none.
  const queue = useRead<number>(market === undefined ? undefined : async () => {
    const res = await runtime.adminKyc.reviewQueue(market);
    return res.ok ? { ok: true, value: res.value.documents.length } : res;
  }, [runtime, market]);
  const supply = useRead<PeriodDashboardView>(market === undefined ? undefined : async () => {
    const window_ = presetPeriod('today', new Date(), timeZone);
    if (window_ === undefined) return { ok: false, error: { code: 'BAD_REQUEST' } };
    return runtime.ops.periodDashboard({ from: window_.from, to: window_.to, market });
  }, [runtime, market, timeZone]);

  const lines = useRead<WorkLineSummaryView>(market === undefined ? undefined : () => runtime.driverRegistry.workLines(market), [runtime, market]);

  const nf = new Intl.NumberFormat(lang);
  const summary = lines.state?.status === 'ready' ? lines.state.value : undefined;
  const lineItems = summary === undefined ? [] : [
    ...WORK_LINE_VIEWS.filter((l) => CORE_LINES.includes(l) || summary.lines[l] > 0).map((l) => ({ key: l, label: v('driver.work_line', l), value: summary.lines[l] })),
    { key: 'NONE', label: t('worklines.panel.none'), value: summary.none },
  ];
  const now = supply.state?.status === 'ready' ? supply.state.value.supplyNow : undefined;
  const presence: readonly FleetPresenceView[] = PRESENCES;

  return (
    <>
      <PageHeader title={t('pdash.title')} subtitle={t('pdash.subtitle')} />

      <Grid min="xs" gap={4}>
        <CountCard label={t('pdash.drivers.total')} state={drivers.state} pick={(v: { total: number }) => v.total} />
        <CountCard label={t('dir.filter.active')} state={drivers.state} pick={(v: { active: number }) => v.active} />
        <CountCard label={t('dir.filter.suspended')} state={drivers.state} pick={(v: { suspended: number }) => v.suspended} />
        <CountCard label={t('pdash.merchants.total')} state={merchants.state} pick={(v: { total: number }) => v.total} />
        {market !== undefined && (
          <>
            <CountCard label={t('pdash.approvals')} state={queue.state} pick={(v: number) => v} hint={t('pdash.approvals.hint')} tone={queue.state?.status === 'ready' && queue.state.value > 0 ? 'warning' : undefined} />
            <CountCard label={t('pdash.online')} state={supply.state?.status === 'ready' && now === undefined ? { status: 'error', code: 'NO_SUPPLY' } : supply.state} pick={(v: PeriodDashboardView) => (v.supplyNow?.AVAILABLE ?? 0) + (v.supplyNow?.IN_RIDE ?? 0)} />
          </>
        )}
      </Grid>
      {market === undefined && (
        <Notice tone="neutral">{t('pdash.supply.needMarket')}</Notice>
      )}

      <Grid min="md" gap={4}>
        <StatusPanel title={t('pdash.drivers.title')} totalLabel={t('pdash.drivers.total')} state={drivers.state} reload={drivers.reload} />
        {market !== undefined && (
          <Panel>
            <Stack gap={4}>
              <SectionTitle level={3} size="sub">{t('pdash.supply.title')}</SectionTitle>
              <BarList
                title={t('pdash.supply.title')}
                items={now === undefined ? [] : presence.map((p) => ({ key: p, label: v('fleet.presence', p), value: now[p], tone: presenceTone(p) }))}
                formatValue={(n) => nf.format(n)}
                loading={supply.state === undefined || supply.state.status === 'loading'}
                loadingLabel={t('ui.loading')}
                emptyLabel={supply.state?.status === 'error' ? t('ui.unavailable') : t('pdash.empty')}
              />
            </Stack>
          </Panel>
        )}
        {market !== undefined && (
          <Panel>
            <Stack gap={4}>
              <SectionTitle level={3} size="sub">{t('worklines.panel.title')}</SectionTitle>
              <BarList
                title={t('worklines.panel.title')}
                items={lineItems}
                formatValue={(n) => nf.format(n)}
                loading={lines.state === undefined || lines.state.status === 'loading'}
                loadingLabel={t('ui.loading')}
                emptyLabel={lines.state?.status === 'error' ? t('ui.unavailable') : t('pdash.empty')}
              />
              <Text as="p" role="caption" tone="tertiary">{t('worklines.panel.hint')}</Text>
            </Stack>
          </Panel>
        )}
        <StatusPanel title={t('pdash.merchants.title')} totalLabel={t('pdash.merchants.total')} state={merchants.state} reload={merchants.reload} />
      </Grid>
    </>
  );
}
